import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { SessionContext } from '@wix/da-react-context/pkg/SessionContext';
import { getSellableAs } from '../../types/deviation/getters';
import { useDeviationAttributes } from '../../DeviationViews/hooks/useDeviationAttributes';
import { getIsCurrentUserAuthorOf } from '../../redux/deviations/selectors';
import { useSubscriptionTier } from '../../utils/hooks/useSubscriptionTier';
import { ElementVisibility, NOT_INCLUDED_VISIBILITY_GROUP, WindowBreakpoint } from './types';
import { getAllowOffers, getHasBuyItNow, getHasOffers, getHasOwnOffer, getHasRejectedOffer, getIsAdoptableOfferType, getIsBeforeOfferEndTime, getIsOnSale, getIsPending } from '../../utils/adoptables';
import { ScreenSizeContext } from '../../Measured';
import { getDeviationType } from '../../DeviationViews/_helpers';
import { DeviationTypes } from '../../types/deviation';
function useIsAuthor(deviation) {
    const isAuthorSelector = useCallback((state)=>getIsCurrentUserAuthorOf(state, deviation), [
        deviation
    ]);
    return useSelector(isAuthorSelector);
}
// TODO: Expand with other common breakpoints
function useWindowBreakpoint() {
    const isMobile = useContext(MobileContext);
    const screenSize = useContext(ScreenSizeContext);
    if (isMobile) {
        return WindowBreakpoint.MobileAverageSize;
    }
    if (screenSize.width >= 1100) {
        return WindowBreakpoint.DesktopAverageSize;
    }
    return WindowBreakpoint.DesktopSmallSize;
}
/**
 * A set of common checks against the adoptable element groups that other controls use to collapse.
 */ function shouldCollapseForAdoptableElementGroups(determinationContext) {
    const { elementGroups: { adoptableBuyer, adoptableSeller }, windowBreakpoint } = determinationContext;
    return(// If the adoptable buyer group has a couple elements, there isn't enough room. Signal that we should collapse this control.
    adoptableBuyer.groupVisibility === ElementVisibility.PrimaryBar && adoptableBuyer.countOnBar >= 2 || // On smaller desktop screens, we'll signal to collapse this control if either adoptable-related element groups are active. Regardless
    // of the number of elements visible from each.
    (adoptableBuyer.groupVisibility === ElementVisibility.PrimaryBar || adoptableSeller.groupVisibility === ElementVisibility.PrimaryBar) && windowBreakpoint === WindowBreakpoint.DesktopSmallSize);
}
function getEditBackgroundVisibility(determinationContext) {
    return determinationContext.deviationAttributes.isBackgroundEditable ? ElementVisibility.PrimaryBar : ElementVisibility.NotIncluded;
}
/**
 * Returns an object that maps ActionBar elements to their level of visibility.
 * (e.g. is this element on the bar? collapsed into the more menu? completely absent?)
 */ export function useElementVisibilityMap(deviation) {
    var _deviation_extended_adoptable, _deviation_extended;
    const windowBreakpoint = useWindowBreakpoint();
    const isAuthor = useIsAuthor(deviation);
    const isMobile = useContext(MobileContext);
    const { isLoggedIn } = useContext(SessionContext);
    const isOwner = !!((_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : (_deviation_extended_adoptable = _deviation_extended.adoptable) === null || _deviation_extended_adoptable === void 0 ? void 0 : _deviation_extended_adoptable.isOwner);
    const deviationAttributes = useDeviationAttributes(deviation, isAuthor);
    // These are collections of buttons. They can take up large amounts of space on the action bar,
    // so we use this object to determine if other more general controls should be removed/collapsed.
    const elementGroups = {
        adoptableBuyer: useAdoptableBuyerVisibilityGroup(deviation, isMobile, isOwner),
        adoptableSeller: useAdoptableSellerVisibilityGroup(deviation, isMobile, isOwner)
    };
    const determinationContext = {
        deviation,
        isAuthor,
        isMobile,
        isLoggedIn,
        deviationAttributes,
        windowBreakpoint,
        elementGroups
    };
    const intermediaryMapping = {
        comment: getCommentVisibility(determinationContext),
        premiumDownload: getPremiumDownloadVisibility(determinationContext),
        freeDownload: getFreeDownloadVisibility(determinationContext),
        addToGroup: getAddToGroupVisibility(determinationContext),
        awardBadge: getAwardBadgeVisibility(determinationContext),
        favourite: getFavouriteVisibility(determinationContext),
        privateCollection: getPrivateCollectionVisibility(determinationContext),
        sellDeviation: getSellDeviationVisibility(determinationContext),
        subscribe: useSubscribeVisibility(determinationContext),
        editBackground: getEditBackgroundVisibility(determinationContext),
        deleteDeviation: getDeleteDeviationVisibility(determinationContext),
        editDeviation: getEditDeviationVisibility(determinationContext),
        commissionStatus: getCommissionStatusVisibility(determinationContext),
        orderCommission: getOrderCommissionVisibility(determinationContext),
        share: getShareVisibility(determinationContext),
        report: getReportVisibility(determinationContext),
        fullscreen: getFullscreenVisibility(determinationContext),
        elementGroups: elementGroups
    };
    intermediaryMapping.meta = {
        // elementVisibilityMap.elementGroups has no controls that appear in menus for the time being, so we're
        // only testing the root values of elementVisibilityMap.
        hasMoreActionsMenu: Object.values(intermediaryMapping).includes(ElementVisibility.Menu)
    };
    return intermediaryMapping;
}
function getDeleteDeviationVisibility(determinationContext) {
    return determinationContext.deviationAttributes.isDeletable ? ElementVisibility.PrimaryBar : ElementVisibility.NotIncluded;
}
function getEditDeviationVisibility(determinationContext) {
    // For now, the tier edit popup doesn't work on mobile
    if (determinationContext.isMobile && getDeviationType(determinationContext.deviation) === DeviationTypes.TIER) {
        return ElementVisibility.NotIncluded;
    }
    return determinationContext.deviationAttributes.isEditable ? ElementVisibility.PrimaryBar : ElementVisibility.NotIncluded;
}
function getCommissionStatusVisibility(determinationContext) {
    const deviationType = getDeviationType(determinationContext.deviation);
    if (deviationType === DeviationTypes.COMMISSION && determinationContext.isMobile && determinationContext.deviationAttributes.isEditable) {
        return ElementVisibility.PrimaryBar;
    }
    return ElementVisibility.NotIncluded;
}
function getOrderCommissionVisibility(determinationContext) {
    const deviationType = getDeviationType(determinationContext.deviation);
    if (deviationType === DeviationTypes.COMMISSION && determinationContext.isMobile && !determinationContext.isAuthor && determinationContext.deviationAttributes.isAuthorCommissionable) {
        return ElementVisibility.PrimaryBar;
    }
    return ElementVisibility.NotIncluded;
}
function getCommentVisibility(determinationContext) {
    const { deviationAttributes: { isCommentable }, elementGroups } = determinationContext;
    if (!isCommentable) {
        return ElementVisibility.NotIncluded;
    }
    if (shouldCollapseForAdoptableElementGroups(determinationContext) || // In addition to the common checks provided by shouldCollapseForAdoptableElementGroups,
    // we want to collapse this control in all cases the adoptableSeller group is on the primary bar.
    elementGroups.adoptableSeller.groupVisibility === ElementVisibility.PrimaryBar) {
        return ElementVisibility.Menu;
    }
    return ElementVisibility.PrimaryBar;
}
function getFreeDownloadVisibility(determinationContext) {
    const { isLoggedIn, isMobile, deviationAttributes: { isDownloadable, isAdoptable } } = determinationContext;
    if (!isDownloadable) {
        return ElementVisibility.NotIncluded;
    }
    if (shouldCollapseForAdoptableElementGroups(determinationContext)) {
        return ElementVisibility.Menu;
    }
    if (isMobile && !isLoggedIn) {
        return isAdoptable ? ElementVisibility.NotIncluded : ElementVisibility.SecondaryBar;
    }
    return ElementVisibility.PrimaryBar;
}
function getPremiumDownloadVisibility(determinationContext) {
    const { deviationAttributes: { isPurchasable } } = determinationContext;
    if (!isPurchasable) {
        return ElementVisibility.NotIncluded;
    }
    if (shouldCollapseForAdoptableElementGroups(determinationContext)) {
        return ElementVisibility.Menu;
    }
    return ElementVisibility.PrimaryBar;
}
function getFavouriteVisibility(determinationContext) {
    return determinationContext.deviationAttributes.isFavouritable ? ElementVisibility.PrimaryBar : ElementVisibility.NotIncluded;
}
function getAddToGroupVisibility(determinationContext) {
    const { deviationAttributes: { isGroupSubmittable }, isAuthor } = determinationContext;
    if (!isGroupSubmittable) {
        return ElementVisibility.NotIncluded;
    }
    if (isAuthor) {
        return ElementVisibility.PrimaryBar;
    }
    return ElementVisibility.Menu;
}
function getAwardBadgeVisibility(determinationContext) {
    const { deviationAttributes: { isCoreBadgeAwardable } } = determinationContext;
    if (!isCoreBadgeAwardable) {
        return ElementVisibility.NotIncluded;
    }
    if (shouldCollapseForAdoptableElementGroups(determinationContext)) {
        return ElementVisibility.Menu;
    }
    return ElementVisibility.PrimaryBar;
}
function getPrivateCollectionVisibility(determinationContext) {
    const { isAuthor } = determinationContext;
    if (isAuthor) {
        return ElementVisibility.NotIncluded;
    }
    if (shouldCollapseForAdoptableElementGroups(determinationContext)) {
        return ElementVisibility.Menu;
    }
    return ElementVisibility.PrimaryBar;
}
function getFullscreenVisibility({ isLoggedIn }) {
    return isLoggedIn ? ElementVisibility.PrimaryBar : ElementVisibility.NotIncluded;
}
function getSellDeviationVisibility(determinationContext) {
    const { deviation, isMobile } = determinationContext;
    const sellableAs = getSellableAs(deviation);
    if (sellableAs.length === 0) {
        return ElementVisibility.NotIncluded;
    }
    return isMobile ? ElementVisibility.SecondaryBar : ElementVisibility.PrimaryBar;
}
function useSubscribeVisibility(determinationContext) {
    const tier = useSubscriptionTier(determinationContext.deviation);
    return !determinationContext.deviationAttributes.hasFlag && !!tier ? ElementVisibility.PrimaryBar : ElementVisibility.NotIncluded;
}
function getShareVisibility(determinationContext) {
    return determinationContext.deviationAttributes.isShareable ? ElementVisibility.Menu : ElementVisibility.NotIncluded;
}
function getReportVisibility(determinationContext) {
    return determinationContext.deviationAttributes.isReportable ? ElementVisibility.Menu : ElementVisibility.NotIncluded;
}
function useAdoptableBuyerVisibilityGroup(deviation, isMobile, isOwner) {
    var _deviation_extended_adoptable, _deviation_extended;
    if (!((_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : (_deviation_extended_adoptable = _deviation_extended.adoptable) === null || _deviation_extended_adoptable === void 0 ? void 0 : _deviation_extended_adoptable.canAdopt) || isOwner || isMobile) {
        return NOT_INCLUDED_VISIBILITY_GROUP;
    }
    const allowOffers = getAllowOffers(deviation);
    const isBeforeOfferEndTime = getIsBeforeOfferEndTime(deviation);
    const hasOwnOffer = getHasOwnOffer(deviation);
    const canMakeNewOffer = allowOffers && isBeforeOfferEndTime && !hasOwnOffer;
    const hasRejectedOffer = getHasRejectedOffer(deviation);
    const canManageExistingOffer = allowOffers && isBeforeOfferEndTime && hasOwnOffer && !hasRejectedOffer;
    const hasBuyItNow = getHasBuyItNow(deviation);
    const isPending = getIsPending(deviation);
    const hasViewOffers = getHasOffers(deviation) && isPending;
    const elements = {
        buyItNow: hasBuyItNow,
        makeOffer: canMakeNewOffer,
        manageOffer: canManageExistingOffer,
        viewOffers: hasViewOffers
    };
    const countOnBar = Object.values(elements).filter((isVisible)=>isVisible).length;
    let groupVisibility = ElementVisibility.NotIncluded;
    if (countOnBar > 0) {
        groupVisibility = isMobile ? ElementVisibility.SecondaryBar : ElementVisibility.PrimaryBar;
    }
    return _object_spread({
        groupVisibility,
        countOnBar
    }, elements);
}
function useAdoptableSellerVisibilityGroup(deviation, isMobile, isOwner) {
    if (!deviation.isAdoptable || isMobile) {
        return NOT_INCLUDED_VISIBILITY_GROUP;
    }
    const isPending = getIsPending(deviation);
    const isOnSale = getIsOnSale(deviation);
    if (// Can't edit a deviation's adoptable sale settings if it isn't on sale
    !isPending && !isOnSale || // Only the current owner can edit an ongoing sale
    !isOwner) {
        return NOT_INCLUDED_VISIBILITY_GROUP;
    }
    const isOfferType = getIsAdoptableOfferType(deviation);
    const elements = {
        endSale: true,
        viewOffers: isOfferType
    };
    const countOnBar = Object.values(elements).filter((isVisible)=>isVisible).length;
    let groupVisibility = ElementVisibility.NotIncluded;
    if (countOnBar > 0) {
        groupVisibility = isMobile ? ElementVisibility.SecondaryBar : ElementVisibility.PrimaryBar;
    }
    return _object_spread({
        groupVisibility,
        countOnBar
    }, elements);
}
